/**
 * PAPER LEGENDS — wire protocol shared by the web client and the Go server.
 *
 * The Go mirror lives in backend/internal/protocol/protocol.go. Any change here
 * must be reflected there; shared/protocol/examples.json is parsed by tests on
 * both sides to keep the two in sync.
 */

export const PROTOCOL_VERSION = 1;

export type Move = 'rock' | 'paper' | 'scissors';
/** A move slot after the round deadline; 'timeout' loses to every real move. */
export type ResolvedMove = Move | 'timeout';
/**
 * Optional stance sent with a move. Guard halves the damage of a lost round
 * (and of a won one); Focus adds +50% to a won round but you take +50% when
 * you lose. Using either puts both on cooldown (rules.json `stances`).
 * Ultimate fires your class move once the ink gauge is full (rules.json
 * `ultimates`); it ignores the stance cooldown and empties the gauge.
 */
export type Stance = 'none' | 'guard' | 'focus' | 'ultimate';

export type SkillId =
  | 'none'
  | 'crayon_smash'
  | 'sharp_edge'
  | 'paper_shield'
  | 'eraser_mend'
  | 'doodle_dodge'
  | 'ink_combo'
  | 'lucky_star'
  | 'highlighter_drain'
  | 'sharpener_spikes'
  | 'glue_guard'
  | 'marker_rage';

export type PersonalityId = 'chaotic' | 'stubborn' | 'shy' | 'sharp' | 'copycat' | 'tricky';

export type RoomMode = 'public' | 'classroom';

/* ------------------------------------------------------------------ */
/* Drawings                                                            */
/* ------------------------------------------------------------------ */

/** One pencil/eraser stroke. Points are flattened [x0, y0, x1, y1, ...] on the drawing grid. */
export interface StrokeDTO {
  /** Colour as #rrggbb. Ignored for eraser strokes. */
  c: string;
  /** Brush size in grid units (1..48). */
  s: number;
  /** true = eraser stroke. */
  e: boolean;
  p: number[];
  /**
   * Brush look; absent = the coloured pencil. 'pixel' strokes list the
   * top-left corners of s×s squares instead of a path.
   */
  b?: BrushKind;
}

export const BRUSH_KINDS = ['pencil', 'marker', 'crayon', 'spray', 'watercolor', 'calligraphy', 'pixel'] as const;
export type BrushKind = (typeof BRUSH_KINDS)[number];

export interface DrawingDTO {
  /** Grid width/height; always 256 in protocol v1. */
  w: number;
  h: number;
  strokes: StrokeDTO[];
}

export const DRAWING_LIMITS = {
  size: 256,
  maxStrokes: 1200,
  maxPoints: 24000,
  minBrush: 1,
  maxBrush: 48,
} as const;

/* ------------------------------------------------------------------ */
/* Fighters                                                            */
/* ------------------------------------------------------------------ */

export interface FighterLook {
  /** Built-in preset art id (hero class or preset monster). */
  preset?: string;
  /** Server drawing id (uploaded through POST /api/drawings). */
  drawingId?: string;
}

/** Combat loadout submitted for PvP. The server validates it; it never trusts client damage. */
export interface FighterDTO {
  name: string;
  kind: 'hero' | 'monster';
  maxHp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
  look: FighterLook;
}

/* ------------------------------------------------------------------ */
/* Monsters (REST sharing)                                             */
/* ------------------------------------------------------------------ */

export interface MonsterRewardDTO {
  exp: number;
  coins: number;
}

export interface MonsterDTO {
  name: string;
  hp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
  personality: PersonalityId;
  reward: MonsterRewardDTO;
  /** public = stat budget enforced; classroom = free stats, never ranked. */
  mode: RoomMode;
  tier: 'doodle' | 'sketch' | 'masterpiece';
  drawing: DrawingDTO | null;
  preset?: string;
}

/* ------------------------------------------------------------------ */
/* REST DTOs                                                           */
/* ------------------------------------------------------------------ */

/** The caller's own account (GET /api/players/me, POST /api/players). */
export interface PlayerDTO {
  id: string;
  name: string;
  trophies: number;
  /** 8 characters from A-Z/2-9 (no 0/O/1/I); shown as XXXX-XXXX. */
  friendCode: string;
  /** Kid mode (under 13): the name is generated by the server, never typed. */
  kid: boolean;
  createdAt: string;
}

/** One friend or pending friend request, seen from the caller. */
export interface FriendDTO {
  id: string;
  name: string;
  trophies: number;
  status: 'pending' | 'accepted';
  /** true for a pending request the other player sent. */
  incoming: boolean;
  /** When the request was sent, or when it was accepted. */
  since: string;
  /** Connected to the WebSocket right now. Always false unless status is "accepted". */
  online: boolean;
}
/** GET /api/friends */
export interface FriendsResponse {
  friends: FriendDTO[];
  incoming: FriendDTO[];
  outgoing: FriendDTO[];
}
/**
 * POST /api/friends/requests — exactly one of the two. A request to someone
 * who already asked you is accepted at once (status "accepted").
 * Other routes: POST /api/friends/{id}/accept → FriendResponse,
 * DELETE /api/friends/{id} → 204 (decline, cancel or unfriend).
 */
export type FriendRequestRequest = { friendCode: string } | { playerId: string };
export interface FriendResponse {
  friend: FriendDTO;
}

export interface CreatePlayerRequest {
  name: string;
  /** Kid-mode account: `name` is ignored and a safe name is generated in `lang`. */
  kid?: boolean;
  lang?: 'en' | 'th';
  /** Version (date) of the terms/privacy/cookie notice the player accepted. */
  consent?: string;
}

/** PATCH /api/players/me: set one of name, reroll (new generated name) or kid. */
export interface PatchPlayerRequest {
  name?: string;
  reroll?: boolean;
  kid?: boolean;
  lang?: 'en' | 'th';
}
/* ---------- Monster contest (weekly) ---------- */

export type ContestStatus = 'pending' | 'approved' | 'rejected' | 'winner';

/** One monster in the weekly contest. */
export interface ContestEntryDTO {
  id: string;
  /** ISO week in Thai time, e.g. "2026-W43". */
  round: string;
  monster: MonsterDTO;
  authorName: string;
  status: ContestStatus;
  mine: boolean;
  voted: boolean;
  /** Once the round is over (Hall of Fame), for moderators, and in the open round once the viewer has voted. */
  votes?: number;
  reports?: number;
  createdAt: string;
}
/** GET /api/contest: the open round as this player sees it. */
export interface ContestResponse {
  round: string;
  endsAt: string;
  maxVotes: number;
  votesLeft: number;
  /** Approved entries, shuffled per player. */
  entries: ContestEntryDTO[];
  /** The player's own entry this round, whatever its status. */
  mine: ContestEntryDTO | null;
}
export interface ContestSubmitRequest {
  monster: MonsterDTO;
}
export interface ContestEntryResponse {
  entry: ContestEntryDTO;
}
/** GET /api/contest/hall (winners, newest round first). */
export interface ContestEntriesResponse {
  entries: ContestEntryDTO[];
}
/* ---------- Community: fan mail, dares, weekly boss ---------- */

/** Stickers a player sends the artist of a monster they fought (no typed text). */
export type Reaction = 'cute' | 'scary' | 'funny' | 'cool' | 'tough';
export const REACTIONS: readonly Reaction[] = ['cute', 'scary', 'funny', 'cool', 'tough'];
/** How a battle against someone's monster ended, seen from the player who fought it. */
export type FightResult = 'win' | 'loss' | 'draw';
/** share = shared monster (ref: share code), hall = Hall of Fame winner or weekly boss (ref: entry id), dare = friend's dare (ref: dare id). */
export type FightKind = 'share' | 'hall' | 'dare';
/** Ready-made notes a dare can carry. */
export type DareNote = 'beat_me' | 'too_easy' | 'new_drawing' | 'my_boss' | 'good_luck';
export const DARE_NOTES: readonly DareNote[] = ['beat_me', 'too_easy', 'new_drawing', 'my_boss', 'good_luck'];

/** POST /api/fights (shared monsters and Hall of Fame winners; dares use /api/dares/{id}/result). */
export interface FightReportRequest {
  kind: 'share' | 'hall';
  ref: string;
  result: FightResult;
}
/** counted is false for a monster I drew myself (no fan mail, so no sticker). */
export interface FightReportResponse {
  counted: boolean;
}
/** POST /api/fights/reaction (204). */
export interface FightReactionRequest {
  kind: FightKind;
  ref: string;
  reaction: Reaction;
}
/** One fan of one of my monsters. */
export interface FanNoteDTO {
  kind: FightKind;
  ref: string;
  monsterName: string;
  fromName: string;
  fights: number;
  /** Seen from the fan: 'win' = they beat my monster. */
  lastResult: FightResult;
  reaction: Reaction | null;
  at: string;
}
/** Every fan of one of my monsters, summed up. */
export interface FanMonsterDTO {
  kind: FightKind;
  ref: string;
  name: string;
  /** Different players who fought it. */
  fans: number;
  fights: number;
  /** Battles my monster won. */
  monsterWins: number;
  /** Fans per sticker (each fan's latest sticker counts once). */
  reactions: Partial<Record<Reaction, number>>;
  lastAt: string;
}
/** GET /api/fanmail */
export interface FanMailResponse {
  monsters: FanMonsterDTO[];
  recent: FanNoteDTO[];
}

export type DareStatus = 'open' | 'beaten';
/** A monster drawn to dare one friend; they fight it whenever they like. */
export interface DareDTO {
  id: string;
  fromId: string;
  fromName: string;
  toId: string;
  toName: string;
  monster: MonsterDTO;
  note: DareNote | null;
  status: DareStatus;
  attempts: number;
  reaction: Reaction | null;
  createdAt: string;
  /** The friend's last attempt. */
  answeredAt: string | null;
}
export interface DareCreateRequest {
  friendId: string;
  monster: MonsterDTO;
  note?: DareNote;
}
export interface DareResultRequest {
  result: FightResult;
}
export interface DareResponse {
  dare: DareDTO;
}
/** GET /api/dares, newest first. */
export interface DaresResponse {
  incoming: DareDTO[];
  outgoing: DareDTO[];
}

/** Boss of the week: last round's champion in a tougher form, with one HP pool for everyone. */
export interface BossDTO {
  entryId: string;
  fromRound: string;
  authorName: string;
  /** Boss stats (stronger than the contest entry). */
  monster: MonsterDTO;
  /** I drew the champion. */
  mine: boolean;
  poolHp: number;
  damage: number;
  fighters: number;
  defeated: boolean;
  myHits: number;
  myDamage: number;
  hitsLeft: number;
}
/** GET /api/boss and POST /api/boss/hits. boss is null until the first contest round has a champion. */
export interface BossResponse {
  round: string;
  endsAt: string;
  boss: BossDTO | null;
}
export interface BossHitRequest {
  round: string;
  damage: number;
  result: FightResult;
}

/* ---------- Classroom and friend notebooks ---------- */

/** Classroom limits (protocol/classroom.go). */
export const CLASS_LIMITS = {
  /** Class codes use the friend-code alphabet. */
  codeLength: 6,
  maxMembers: 40,
  /** Battles per player that count towards the class goal each week. */
  battleCap: 20,
  /** Weekly goal: max(goalMin, members × goalPerMember) battles. */
  goalPerMember: 5,
  goalMin: 10,
  /** Newest shared monsters shown per player on a class board or friend's notebook. */
  shelfPerPlayer: 6,
} as const;
/** Hero classes a notebook card may name (HERO_CLASSES in game/data/presets.ts). */
export const HERO_CLASS_IDS = ['pencil_knight', 'crayon_mage', 'eraser_ninja', 'ruler_guard', 'glue_alchemist', 'highlighter_scout'] as const;

/** One monster a player shared, on a class board or in a friend's notebook. */
export interface SharedShelfDTO {
  code: string;
  monster: MonsterDTO;
  createdAt: string;
}
export interface ClassMemberDTO {
  id: string;
  name: string;
  trophies: number;
  owner: boolean;
  joinedAt: string;
  /** This member's battles towards this week's goal (capped). */
  weekBattles: number;
  /** Newest shared monsters (class members only). */
  shared: SharedShelfDTO[];
}
/** This week's class goal: battles by all members together. */
export interface ClassGoalDTO {
  round: string;
  endsAt: string;
  target: number;
  progress: number;
  myBattles: number;
  battleCap: number;
  done: boolean;
}
/** The class board as one member sees it. The name is generated from safe words, never typed. */
export interface ClassDTO {
  id: string;
  code: string;
  name: string;
  ownerId: string;
  /** I run this class. */
  mine: boolean;
  maxMembers: number;
  createdAt: string;
  members: ClassMemberDTO[];
  goal: ClassGoalDTO;
}
/** GET /api/class and the class actions; class is null when I am not in one. */
export interface ClassResponse {
  class: ClassDTO | null;
}
/** POST /api/class and POST /api/class/name (owner: new generated name). */
export interface ClassCreateRequest {
  lang?: string;
}
/** POST /api/class/join */
export interface ClassJoinRequest {
  code: string;
}
/** POST /api/class/battles: a battle ended this week. */
export interface ClassBattleRequest {
  round: string;
}
/** counted is false past the weekly cap; goal is null outside a class. */
export interface ClassBattleResponse {
  counted: boolean;
  goal: ClassGoalDTO | null;
}
/** POST /api/notebook/card (204): what friends see of my notebook. cosmetics counts every owned cosmetic, stamps included. */
export interface NotebookCardDTO {
  /** '' with heroLevel 0 when there is no hero. */
  heroClass: (typeof HERO_CLASS_IDS)[number] | '';
  heroLevel: number;
  stamps: number;
  cosmetics: number;
}
export interface HallWinDTO {
  entryId: string;
  round: string;
  monsterName: string;
}
/** GET /api/friends/{id}/notebook (friends only). card is null until their game sent one. */
export interface FriendNotebookResponse {
  id: string;
  name: string;
  trophies: number;
  card: NotebookCardDTO | null;
  updatedAt: string | null;
  hallWins: HallWinDTO[];
  shared: SharedShelfDTO[];
}

export interface CreatePlayerResponse {
  player: PlayerDTO;
  token: string;
}

/* ---------- accounts & cloud save (Cloudflare server) ---------- */

/** GET /api/config: public settings the client needs. googleClientId is null when Google sign-in is off. */
export interface ConfigResponse {
  googleClientId: string | null;
}
/** POST /api/auth/google and POST /api/auth/google/link: a Google Identity Services ID token. */
export interface GoogleAuthRequest {
  credential: string;
  kid?: boolean;
  lang?: 'en' | 'th';
  /** Version (date) of the terms/privacy/cookie notice the player accepted. */
  consent?: string;
}
/** POST /api/auth/google: signs in (created = a new notebook was made for this Google account). */
export interface GoogleAuthResponse {
  player: PlayerDTO;
  token: string;
  created: boolean;
  email: string;
}
/** GET /api/account: how this player can sign in on another device. */
export interface AccountResponse {
  /** The linked Google account's email, or null for a guest. */
  google: string | null;
}
/** GET /api/save: the cloud copy of the notebook (save null = none yet, rev 0). */
export interface CloudSaveResponse {
  save: unknown;
  rev: number;
  /** Unix epoch milliseconds (0 = never). */
  updatedAt: number;
}
/** PUT /api/save: baseRev must be the rev the client last saw, or the server answers 409. */
export interface PutSaveRequest {
  save: unknown;
  baseRev: number;
}
export interface PutSaveResponse {
  rev: number;
  updatedAt: number;
}

export interface PlayerResponse {
  player: PlayerDTO;
}

export interface LeaderboardEntryDTO {
  id: string;
  name: string;
  trophies: number;
}
export interface LeaderboardResponse {
  players: LeaderboardEntryDTO[];
}

/**
 * Server rankings. trophies: online battles (server-decided); stars: story
 * stars (max 90); speedrun: fastest whole-story finish, in ms (lower is better);
 * tower: highest Homework Tower floor; level: highest hero level.
 */
export type RankBoard = 'trophies' | 'stars' | 'speedrun' | 'tower' | 'level';
export const RANK_BOARDS: readonly RankBoard[] = ['trophies', 'stars', 'speedrun', 'tower', 'level'];
export interface RankRowDTO {
  rank: number;
  id: string;
  name: string;
  score: number;
}
export interface RankingsResponse {
  board: RankBoard;
  rows: RankRowDTO[];
  /** The signed-in player's place, or null when signed out or not on this board yet. */
  me: { rank: number; score: number } | null;
  /** How many players are on this board. */
  total: number;
}
export interface BattlesResponse {
  battles: BattleRecordDTO[];
}
export interface GetDrawingResponse {
  id: string;
  drawing: DrawingDTO;
}

export interface UploadDrawingRequest {
  drawing: DrawingDTO;
}
export interface UploadDrawingResponse {
  id: string;
}

export interface ShareMonsterRequest {
  monster: MonsterDTO;
}
export interface ShareMonsterResponse {
  code: string;
  monster: MonsterDTO;
}
export interface GetSharedMonsterResponse {
  code: string;
  monster: MonsterDTO;
  authorName: string;
  createdAt: string;
}

export interface BattleRecordDTO {
  id: string;
  roomCode: string;
  mode: RoomMode;
  ranked: boolean;
  players: { id: string; name: string }[];
  winnerId: string | null;
  reason: BattleEndReason;
  rounds: number;
  endedAt: string;
}

export interface HealthResponse {
  ok: boolean;
  version: string;
  protocol: number;
  storage: 'memory' | 'postgres' | 'd1';
}

export interface ApiErrorResponse {
  error: { code: ErrorCode; message: string };
}

/* ------------------------------------------------------------------ */
/* Rooms & battles (WebSocket)                                         */
/* ------------------------------------------------------------------ */

export interface RoomRules {
  turnTimeoutMs: number;
  maxRounds: number;
  /** Classroom only: skip the stat budget (range checks still apply). */
  freeStats: boolean;
}

export const CLASSROOM_RULE_LIMITS = {
  turnTimeoutMs: { min: 5000, max: 60000 },
  maxRounds: { min: 3, max: 50 },
} as const;

export interface RoomPlayerView {
  id: string;
  name: string;
  fighter: FighterDTO;
  connected: boolean;
}

export type RoomStatus = 'waiting' | 'lobby' | 'battle';

export interface RoomView {
  code: string;
  mode: RoomMode;
  hostId: string;
  status: RoomStatus;
  rules: RoomRules;
  players: RoomPlayerView[];
  /** Player id that has an outstanding challenge, if any. */
  challengerId: string | null;
}

export interface RoundResultView {
  round: number;
  /** Indexed by seat (players[0], players[1]). */
  moves: [ResolvedMove, ResolvedMove];
  /** Seat index of the round winner, or -1 for a tie. */
  winner: -1 | 0 | 1;
  damage: number;
  crit: boolean;
  dodged: boolean;
  shielded: boolean;
  /** HP restored per seat (Eraser Mend on a tie, Highlighter Drain on a win). */
  healed: [number, number];
  /** Damage bounced back onto the round winner by Sharpener Spikes. */
  recoil: number;
  hp: [number, number];
  combo: [number, number];
  /** Stances actually applied this round ('none' when cooling down or timed out). */
  stances: [Stance, Stance];
  /** Ink per seat after the round (0..rules.ink.max). */
  ink: [number, number];
  /** Damage dealt by each seat's ultimate this round (heals go to `healed`). */
  ult: [number, number];
  /** Seat whose combo chain burst this round, or null. */
  burst: 0 | 1 | null;
  /** Damage of that burst (0 when none). */
  burstDamage: number;
  /** Each seat's combo chain after the round: its last all-different winning moves. */
  chain: [Move[], Move[]];
}

export type BattleEndReason = 'ko' | 'rounds' | 'forfeit' | 'timeout' | 'disconnect';

export interface BattleView {
  id: string;
  ranked: boolean;
  rules: RoomRules;
  /** 1-based number of the round currently waiting for moves. */
  round: number;
  players: { id: string; name: string; fighter: FighterDTO }[];
  hp: [number, number];
  combo: [number, number];
  /** Rounds each seat must still wait before it can use a stance again (0 = ready). */
  stanceCooldown: [number, number];
  /** Ink per seat; the ultimate is ready at rules.ink.max. */
  ink: [number, number];
  /** Each seat's current combo chain. */
  chain: [Move[], Move[]];
  /** Unix epoch milliseconds when the current round auto-resolves. */
  deadline: number;
  /** Seats that have locked a move this round (never the move itself). */
  locked: [boolean, boolean];
  log: RoundResultView[];
}

export type ErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'invalid_state'
  | 'invalid_move'
  | 'wrong_round'
  | 'already_locked'
  | 'room_full'
  | 'budget_exceeded'
  | 'duplicate'
  | 'internal';

/* ---------- client -> server ---------- */

interface Req<T extends string> {
  t: T;
  /** Client request id, unique per connection; used for dedupe and acks. */
  id: string;
}

export interface AuthMsg extends Req<'auth'> {
  token: string;
}
export interface RoomCreateMsg extends Req<'room.create'> {
  mode: RoomMode;
  rules?: Partial<RoomRules>;
  fighter: FighterDTO;
}
export interface RoomJoinMsg extends Req<'room.join'> {
  code: string;
  fighter: FighterDTO;
}
export type RoomLeaveMsg = Req<'room.leave'>;
export interface RoomFighterMsg extends Req<'room.fighter'> {
  fighter: FighterDTO;
}
export type ChallengeSendMsg = Req<'challenge.send'>;
export interface ChallengeRespondMsg extends Req<'challenge.respond'> {
  accept: boolean;
}
export interface BattleMoveMsg extends Req<'battle.move'> {
  battleId: string;
  round: number;
  move: Move;
  /** Omitted = 'none'. A stance sent while cooling down is applied as 'none'. */
  stance?: Stance;
}
export interface BattleForfeitMsg extends Req<'battle.forfeit'> {
  battleId: string;
}
export type PingMsg = Req<'ping'>;
/**
 * Invite an online friend to battle. Reuses your room while you wait alone in
 * it; otherwise creates a public room with `fighter` (required then).
 */
export interface InviteSendMsg extends Req<'invite.send'> {
  friendId: string;
  fighter?: FighterDTO;
}
/** Answer a friend's invite. Accepting joins their room with `fighter` (required then). */
export interface InviteRespondMsg extends Req<'invite.respond'> {
  fromId: string;
  accept: boolean;
  fighter?: FighterDTO;
}

/* ---------- world chat (13+; kid-mode accounts can neither read nor write) ---------- */

/** Start receiving the world chat; the server answers with chat.history. */
export type ChatJoinMsg = Req<'chat.join'>;
/** Stop receiving the world chat. */
export type ChatLeaveMsg = Req<'chat.leave'>;
/** Say something in the world chat (rude words are masked by the server). */
export interface ChatSendMsg extends Req<'chat.send'> {
  text: string;
}
/** Report a message; enough reports hide it until a moderator looks. */
export interface ChatReportMsg extends Req<'chat.report'> {
  messageId: string;
}
/** Quick match: battle whoever else is looking (public, ranked); a bot steps in if nobody comes. */
export interface MatchFindMsg extends Req<'match.find'> {
  fighter: FighterDTO;
}
export type MatchCancelMsg = Req<'match.cancel'>;

export type ClientMessage =
  | AuthMsg
  | RoomCreateMsg
  | RoomJoinMsg
  | RoomLeaveMsg
  | RoomFighterMsg
  | ChallengeSendMsg
  | ChallengeRespondMsg
  | BattleMoveMsg
  | BattleForfeitMsg
  | PingMsg
  | InviteSendMsg
  | InviteRespondMsg
  | ChatJoinMsg
  | ChatLeaveMsg
  | ChatSendMsg
  | ChatReportMsg
  | MatchFindMsg
  | MatchCancelMsg;

/* ---------- server -> client ---------- */

export interface AckMsg {
  t: 'ack';
  id: string;
}
export interface ErrorMsg {
  t: 'error';
  id?: string;
  code: ErrorCode;
  message: string;
}
export interface WelcomeMsg {
  t: 'welcome';
  player: { id: string; name: string };
  protocol: number;
  /** Set when the player was re-attached to a room after reconnecting. */
  resumedRoom: string | null;
}
export interface RoomStateMsg {
  t: 'room.state';
  room: RoomView | null;
}
export interface ChallengeIncomingMsg {
  t: 'challenge.incoming';
  fromId: string;
  fromName: string;
}
export interface ChallengeDeclinedMsg {
  t: 'challenge.declined';
  byId: string;
}
export interface BattleStartMsg {
  t: 'battle.start';
  battle: BattleView;
}
export interface BattleSyncMsg {
  t: 'battle.sync';
  battle: BattleView;
}
export interface BattleLockedMsg {
  t: 'battle.locked';
  battleId: string;
  round: number;
  seat: 0 | 1;
}
export interface BattleRoundMsg {
  t: 'battle.round';
  battleId: string;
  result: RoundResultView;
  /** Deadline of the next round (0 once the battle has ended). */
  deadline: number;
}
export interface BattleEndMsg {
  t: 'battle.end';
  battleId: string;
  winnerId: string | null;
  reason: BattleEndReason;
  ranked: boolean;
  /** Trophy change per player id (empty for unranked battles). */
  trophies: Record<string, number>;
}
export interface PongMsg {
  t: 'pong';
  id: string;
  serverTime: number;
}
/** Someone sent you a friend request (`friend` is them, seen from you: pending, incoming). */
export interface FriendRequestMsg {
  t: 'friend.request';
  friend: FriendDTO;
}
/** You and `friend` just became friends (they accepted, or asked you back). */
export interface FriendAcceptedMsg {
  t: 'friend.accepted';
  friend: FriendDTO;
}
/** The other player declined, cancelled or unfriended. */
export interface FriendRemovedMsg {
  t: 'friend.removed';
  id: string;
}
/** A friend connected or disconnected. */
export interface FriendOnlineMsg {
  t: 'friend.online';
  id: string;
  online: boolean;
}
export interface InviteView {
  fromId: string;
  fromName: string;
  roomCode: string;
  /** Unix epoch milliseconds; the invite cannot be accepted after this. */
  expiresAt: number;
}
/** A friend invites you to battle in their room. */
export interface InviteIncomingMsg {
  t: 'invite.incoming';
  invite: InviteView;
}
/** Your invite was declined. */
export interface InviteDeclinedMsg {
  t: 'invite.declined';
  byId: string;
}
/** A friend sent you a dare. */
export interface DareReceivedMsg {
  t: 'dare.received';
  dare: DareDTO;
}
/** Your friend fought your dare (or sent a sticker for it). */
export interface DareAnsweredMsg {
  t: 'dare.answered';
  dare: DareDTO;
}
/** The sender took a dare back. */
export interface DareRemovedMsg {
  t: 'dare.removed';
  id: string;
}
/** Someone sent a sticker for one of your monsters. */
export interface FanMailMsg {
  t: 'fan.mail';
  note: FanNoteDTO;
}

export interface ChatMessageView {
  id: string;
  playerId: string;
  name: string;
  /** Already filtered: rude words arrive masked with '*'. */
  text: string;
  /** Unix epoch milliseconds. */
  at: number;
}
/** The latest messages, oldest first (after chat.join). */
export interface ChatHistoryMsg {
  t: 'chat.history';
  messages: ChatMessageView[];
}
export interface ChatMessageMsg {
  t: 'chat.message';
  message: ChatMessageView;
}
/** A moderator (or enough reports) removed a message. */
export interface ChatDeletedMsg {
  t: 'chat.deleted';
  id: string;
}

export const CHAT_LIMITS = {
  /** Characters (UTF-16 code units) per message. */
  maxLength: 200,
  /** Messages sent with chat.history. */
  history: 50,
  /** Minimum time between two messages of one player. */
  cooldownMs: 1500,
  /** Reports that hide a message. */
  reportsToHide: 3,
} as const;

/** Quick match progress: searching (with when a bot steps in) or not searching any more. */
export interface MatchStatusMsg {
  t: 'match.status';
  searching: boolean;
  /** Epoch ms when a bot takes the seat if no player turned up. */
  botAt?: number;
}

export type ServerMessage =
  | MatchStatusMsg
  | AckMsg
  | ErrorMsg
  | WelcomeMsg
  | RoomStateMsg
  | ChallengeIncomingMsg
  | ChallengeDeclinedMsg
  | BattleStartMsg
  | BattleSyncMsg
  | BattleLockedMsg
  | BattleRoundMsg
  | BattleEndMsg
  | PongMsg
  | FriendRequestMsg
  | FriendAcceptedMsg
  | FriendRemovedMsg
  | FriendOnlineMsg
  | InviteIncomingMsg
  | InviteDeclinedMsg
  | DareReceivedMsg
  | DareAnsweredMsg
  | DareRemovedMsg
  | FanMailMsg
  | ChatHistoryMsg
  | ChatMessageMsg
  | ChatDeletedMsg;

export const WS_LIMITS = {
  maxMessageBytes: 16 * 1024,
  authTimeoutMs: 5000,
  /** Token bucket: sustained messages per second and burst size. */
  ratePerSec: 8,
  burst: 16,
} as const;

/** How long a battle invite can be accepted. One pending invite per pair of friends. */
export const INVITE_TTL_MS = 60000;
